import type {
  ConnectedWallet,
  EIP1193Provider,
  UseCrossAppAccountsInterface,
} from "@privy-io/react-auth"
import {
  http,
  type Chain,
  ProviderRpcError,
  type Transport,
  numberToHex,
} from "viem"

/**
 * Privy lists no wallet in `useWallets()` for a user who logged in with
 * another Privy app's wallet (VeeFriends), so `@privy-io/wagmi` leaves wagmi
 * disconnected and every buy waits for a wallet client that never arrives.
 * This turns the cross-app account into a wallet that bridge can connect:
 * signatures and transactions go through `useCrossAppAccounts` (a popup on the
 * provider app), reads go to our RPC.
 */
export type CrossAppSigner = Pick<
  UseCrossAppAccountsInterface,
  "sendTransaction" | "signMessage" | "signTypedData"
>

type CrossAppTransaction = Parameters<CrossAppSigner["sendTransaction"]>[0]
type CrossAppTypedData = Parameters<CrossAppSigner["signTypedData"]>[0]
type ProviderListener = Parameters<EIP1193Provider["on"]>[1]

export interface CrossAppWalletOptions {
  /** The embedded wallet address exactly as Privy lists it on the account. */
  address: string
  chains: readonly [Chain, ...Chain[]]
  transports: Partial<Record<number, Transport>>
  /** Read at call time so the provider always uses Privy's latest session. */
  getSigner: () => CrossAppSigner
  meta: { id: string; name: string; icon?: string }
}

export function createCrossAppConnectedWallet(
  options: CrossAppWalletOptions,
): ConnectedWallet {
  const { address, getSigner, meta } = options
  const provider = createCrossAppProvider(options)

  return {
    type: "ethereum",
    address,
    get chainId() {
      return `eip155:${provider.getChainId()}`
    },
    connectedAt: Date.now(),
    walletClientType: "unknown",
    connectorType: "injected",
    imported: false,
    meta,
    linked: true,
    isConnected: async () => true,
    disconnect: () => {},
    switchChain: async (targetChainId) => {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [
          {
            chainId:
              typeof targetChainId === "number"
                ? numberToHex(targetChainId)
                : targetChainId,
          },
        ],
      })
    },
    getEthereumProvider: async () => provider,
    sign: (message) => getSigner().signMessage(message, { address }),
    loginOrLink: async () => {},
    unlink: async () => {},
    fund: async () => {},
  }
}

export function createCrossAppProvider({
  address,
  chains,
  transports,
  getSigner,
}: CrossAppWalletOptions) {
  let chainId = chains[0].id
  const listeners = new Map<string, Set<ProviderListener>>()

  const emit = (eventName: string, payload: unknown) => {
    for (const listener of listeners.get(eventName) ?? []) {
      Reflect.apply(listener, undefined, [payload])
    }
  }

  const readRpc = (method: string, params: unknown) => {
    const chain = chains.find((item) => item.id === chainId) ?? chains[0]
    const transport = transports[chain.id] ?? http()
    return transport({ chain }).request({ method, params })
  }

  const request = async ({
    method,
    params,
  }: {
    method: string
    params?: readonly unknown[]
  }): Promise<unknown> => {
    switch (method) {
      case "eth_accounts":
      case "eth_requestAccounts":
        return [address]
      case "wallet_requestPermissions":
        return [
          {
            parentCapability: "eth_accounts",
            caveats: [{ type: "restrictReturnedAccounts", value: [address] }],
          },
        ]
      case "wallet_revokePermissions":
        return null
      case "eth_chainId":
        return numberToHex(chainId)
      case "wallet_switchEthereumChain": {
        const target = Number(readField(params?.[0], "chainId"))
        if (!chains.some((chain) => chain.id === target)) {
          throw new ProviderRpcError(new Error("Unrecognized chain"), {
            code: 4902,
            shortMessage: `Chain ${target} is not supported`,
          })
        }
        if (target !== chainId) {
          chainId = target
          emit("chainChanged", numberToHex(target))
        }
        return null
      }
      case "eth_sendTransaction":
        return withUserRejection(() =>
          getSigner().sendTransaction(toCrossAppTransaction(params?.[0]), {
            address,
          }),
        )
      case "personal_sign":
        return withUserRejection(() =>
          getSigner().signMessage(String(params?.[0]), { address, chainId }),
        )
      case "eth_signTypedData_v4":
        return withUserRejection(() =>
          getSigner().signTypedData(toTypedData(params?.[1]), {
            address,
            chainId,
          }),
        )
      default:
        // Wallet and signing methods the provider app can't serve must not
        // reach the public RPC.
        if (method.startsWith("wallet_") || method.startsWith("eth_sign")) {
          throw new ProviderRpcError(new Error("Unsupported method"), {
            code: 4200,
            shortMessage: `${method} is not supported by this wallet`,
          })
        }
        return readRpc(method, params)
    }
  }

  const toCrossAppTransaction = (value: unknown): CrossAppTransaction => ({
    // The provider app signs for the account address, casing included.
    from: address,
    to: readString(value, "to"),
    data: readString(value, "data"),
    value: readString(value, "value"),
    nonce: readString(value, "nonce"),
    gasLimit: readString(value, "gas"),
    gasPrice: readString(value, "gasPrice"),
    maxFeePerGas: readString(value, "maxFeePerGas"),
    maxPriorityFeePerGas: readString(value, "maxPriorityFeePerGas"),
    chainId,
  })

  return {
    request,
    on: (eventName: string, listener: ProviderListener) => {
      const set = listeners.get(eventName) ?? new Set()
      set.add(listener)
      listeners.set(eventName, set)
    },
    removeListener: (
      eventName: string | symbol,
      listener: ProviderListener,
    ) => {
      if (typeof eventName === "string") {
        listeners.get(eventName)?.delete(listener)
      }
    },
    getChainId: () => chainId,
  }
}

// Privy rejects with "User rejected request" when the popup is closed; wagmi
// and the buy flow recognise a rejection only by EIP-1193 code 4001.
async function withUserRejection<T>(send: () => Promise<T>): Promise<T> {
  try {
    return await send()
  } catch (error) {
    if (error instanceof Error && /rejected/i.test(error.message)) {
      throw new ProviderRpcError(error, {
        code: 4001,
        shortMessage: "User rejected request",
      })
    }
    throw error
  }
}

function toTypedData(value: unknown): CrossAppTypedData {
  if (typeof value !== "string") {
    throw new ProviderRpcError(new Error("Invalid typed data"), {
      code: -32602,
      shortMessage: "eth_signTypedData_v4 expects typed data as JSON",
    })
  }
  const parsed: CrossAppTypedData = JSON.parse(value)
  return parsed
}

function readField(value: unknown, key: string): unknown {
  if (typeof value !== "object" || value === null || !(key in value)) {
    return undefined
  }
  return Reflect.get(value, key)
}

function readString(value: unknown, key: string): string | undefined {
  const field = readField(value, key)
  return typeof field === "string" ? field : undefined
}
